/**
 * Right panel — Lab Aggregate `aggregate-net-exposure-tracker` book tree.
 *
 * Lab Aggregator (betabackend-aggregate) deliberately hides match/bookmaker
 * ladder + View Downline (HTML-commented in aggregate-net-exposure-tracker.html).
 * Fancy/session keeps PT ladder only — no family expand.
 */

import { useState } from 'react'
import {
  useDownlineParticularBooks,
  useFancyPtLadder,
} from '@/api/hooks/net-exposure/useWorkstation'
import { useSession } from '@/api/hooks/shared/useSession'
import { useViewerCurrency } from '@/api/hooks/shared/useViewerCurrency'
import { useNetExposureDisplayCurrency } from '@/features/net-exposure/useNetExposureDisplayCurrency'
import { lineSessionMoneyOpts } from '@/features/net-exposure/lineSessionDisplayMoney'
import { money } from '@/lib/format'
import { isLineSessionMarket, type FancyPtLadderRow } from '@/api/map/workstation'
import type { BookType } from '../fixture'
import {
  treeHorseAmount,
  treeNetExposure,
  type WorkstationBook,
  type WorkstationMarketType,
  type WorkstationRunner,
  type WorkstationTreeNode,
} from '../workstation-fixture'
import { visibleMatchRunners } from './visibleRunners'

/** Lab Aggregate: View Downline is commented out on the Aggregate tracker. */
export function aggregateWorkstationShowsDownline(): boolean {
  return false
}

/** Lab Aggregate: ladder icon only on fancy / lineLadder rows. */
export function aggregateWorkstationShowsPtLadder(type: WorkstationMarketType): boolean {
  return type === 'fancy'
}

const ICON = {
  levelUp: '/net-exposure/level-up.svg',
  familyTree: '/net-exposure/family-tree-primary.svg',
  familyTreeFancy: '/net-exposure/family-tree-primary.svg',
  ladder: '/net-exposure/ladder.svg',
  closeDownline: '/net-exposure/sort-amount-asc.svg',
} as const

function toneClass(value: number): string {
  return value < 0 ? 'w7-ws-amt-neg' : 'w7-ws-amt-pos'
}

function Amt({
  value,
  bookType,
  level,
  marketType,
  lineSession = false,
}: {
  value: number
  bookType: BookType
  level: string
  marketType: WorkstationMarketType
  /** Betfair line session only — Lab dashboard convert; skip POINT@1. */
  lineSession?: boolean
}) {
  const { data } = useSession()
  const bookCurrency = useNetExposureDisplayCurrency(marketType)
  const lsOpts = lineSessionMoneyOpts(data?.account.currencyType)
  const currency = lineSession ? lsOpts.currency : bookCurrency
  const body = money(value, {
    round: true,
    symbol: false,
    currency,
    ...(lineSession ? { alreadyDisplay: lsOpts.alreadyDisplay } : {}),
  })
  const prefix =
    bookType === 'My PT' && level !== 'player' && currency === 'INR' ? 'Rs.' : ''
  const suffix = currency !== 'INR' ? ' Pts' : ''
  return (
    <span className={toneClass(value)}>
      {prefix}
      {body}
      {suffix}
    </span>
  )
}

function padRunnerCount(count: number, type?: WorkstationMarketType): number {
  // Fancy with no No|Yes horses yet: do not invent a blank Match Odds column.
  if (type === 'fancy' && count === 0) return 0
  // Two-runner Match Odds markets should not invent a blank Draw/X column.
  return Math.max(1, count)
}

export function ladderRunsLabel(row: FancyPtLadderRow): string {
  if (row.endScore === 'Infinite') return `${row.startScore} +`
  return `${row.startScore} - ${row.endScore}`
}

/** Lab PT ladder icon — one open row at a time (toggle off closes). */
export function nextPtLadderOpen(prev: string | null, id: string): string | null {
  return prev === id ? null : id
}

/** Lab `PT-ladder` strip under a fancy/session tree row. */
export function PtLadderPanel({
  rows,
  loading,
  cols,
  lineSession = false,
}: {
  rows: FancyPtLadderRow[]
  loading: boolean
  cols: number
  lineSession?: boolean
}) {
  const currency = useViewerCurrency()
  const { data } = useSession()
  const lsOpts = lineSessionMoneyOpts(data?.account.currencyType)
  const ladderCurrency = lineSession ? lsOpts.currency : currency
  const ladderAlready = lineSession ? lsOpts.alreadyDisplay : false
  return (
    <tr className="w7-ws-pt-ladder-row">
      <td className="w7-ws-pt-ladder" colSpan={3 + cols}>
        {loading && rows.length === 0 ? (
          <div className="w7-ws-pt-ladder-empty">Loading ladder…</div>
        ) : rows.length === 0 ? (
          <div className="w7-ws-pt-ladder-empty">No ladder</div>
        ) : (
          <table className="w7-ws-pt-ladder-table">
            <thead>
              <tr>
                <th>Runs</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.startScore}-${row.endScore}`}>
                  <td>{ladderRunsLabel(row)}</td>
                  <td className={toneClass(row.amount)}>
                    {money(row.amount, {
                      round: true,
                      symbol: false,
                      currency: ladderCurrency,
                      alreadyDisplay: ladderAlready,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </td>
    </tr>
  )
}

function TreeRow({
  node,
  runners,
  type,
  bookType,
  depth,
  open,
  onToggle,
  marketId,
  live,
  ladderOpen,
  onToggleLadder,
  lineSession,
}: {
  node: WorkstationTreeNode
  runners: WorkstationRunner[]
  type: WorkstationMarketType
  bookType: BookType
  depth: number
  open: Set<string>
  onToggle: (id: string) => void
  marketId: string
  live: boolean
  ladderOpen: string | null
  onToggleLadder: (id: string) => void
  /** Betfair line session — ladder type betfair-line-sessions. */
  lineSession: boolean
}) {
  // Lab Aggregate: no View Downline; fancy keeps PT ladder only.
  const canExpand = aggregateWorkstationShowsDownline() && node.level !== 'player'
  const showLadderBtn = aggregateWorkstationShowsPtLadder(type)
  const isOpen = open.has(node.id)
  const ne = treeNetExposure(node, type, bookType)
  const cols = padRunnerCount(runners.length, type)
  const treeIcon = type === 'fancy' ? ICON.familyTreeFancy : ICON.familyTree
  const showLadder = showLadderBtn && ladderOpen === node.id
  const ladderQ = useFancyPtLadder({
    marketId,
    userId: node.id,
    accessLevel: node.accessLevel,
    selectedType: bookType,
    nodeLevel: node.level,
    enabled: live && showLadder,
    lineSession,
  })

  return (
    <>
      <tr className="w7-ws-tree-row" data-depth={depth} data-level={node.level}>
        <td className="w7-ws-tree-user" style={{ paddingLeft: `${8 + depth * 16}px` }}>
          <img
            className={`w7-ws-icon w7-ws-icon-level${node.level === 'player' ? ' is-player' : ''}`}
            src={ICON.levelUp}
            alt=""
            width={14}
            height={14}
          />
          <span className="w7-ws-tree-name">{node.username}</span>
        </td>
        <td className="w7-ws-tree-expand">
          <span className="w7-ws-tree-actions">
            {showLadderBtn ? (
              <button
                type="button"
                className={`w7-ws-icon-btn${showLadder ? ' is-active' : ''}`}
                title="PT ladder"
                aria-pressed={showLadder}
                onClick={() => onToggleLadder(node.id)}
              >
                <img className="w7-ws-icon" src={ICON.ladder} alt="" width={16} height={16} />
              </button>
            ) : null}
            {canExpand ? (
              <button
                type="button"
                className="w7-ws-tree-toggle"
                aria-expanded={isOpen}
                title={isOpen ? 'Close Downline Book' : 'View Downline Book'}
                onClick={() => onToggle(node.id)}
              >
                <img
                  className="w7-ws-icon"
                  src={isOpen ? ICON.closeDownline : treeIcon}
                  alt=""
                  width={16}
                  height={16}
                />
              </button>
            ) : null}
          </span>
        </td>
        <td className="w7-ws-tree-ne">
          <Amt
            value={ne}
            bookType={bookType}
            level={node.level}
            marketType={type}
            lineSession={lineSession}
          />
        </td>
        {Array.from({ length: cols }, (_, i) => {
          const runner = runners[i]
          if (!runner) {
            return (
              <td key={`pad-${i}`} className="w7-ws-tree-cell">
                <span className="w7-ne-dash">-</span>
              </td>
            )
          }
          const amount = treeHorseAmount(node, runner.id, bookType)
          return (
            <td key={runner.id} className="w7-ws-tree-cell">
              <Amt
                value={amount}
                bookType={bookType}
                level={node.level}
                marketType={type}
                lineSession={lineSession}
              />
            </td>
          )
        })}
      </tr>
      {showLadder ? (
        <PtLadderPanel
          rows={ladderQ.data?.ladder ?? []}
          loading={ladderQ.isLoading || ladderQ.isFetching}
          cols={cols}
          lineSession={lineSession}
        />
      ) : null}
      {canExpand && isOpen ? (
        <DownlineBranch
          parent={node}
          runners={runners}
          type={type}
          bookType={bookType}
          depth={depth}
          open={open}
          onToggle={onToggle}
          marketId={marketId}
          live={live}
          ladderOpen={ladderOpen}
          onToggleLadder={onToggleLadder}
          lineSession={lineSession}
        />
      ) : null}
    </>
  )
}

/** Lab recursive `<net-exposure-tracker>` children under an expanded row. */
function DownlineBranch({
  parent,
  runners,
  type,
  bookType,
  depth,
  open,
  onToggle,
  marketId,
  live,
  ladderOpen,
  onToggleLadder,
  lineSession,
}: {
  parent: WorkstationTreeNode
  runners: WorkstationRunner[]
  type: WorkstationMarketType
  bookType: BookType
  depth: number
  open: Set<string>
  onToggle: (id: string) => void
  marketId: string
  live: boolean
  ladderOpen: string | null
  onToggleLadder: (id: string) => void
  lineSession: boolean
}) {
  const seeded = parent.children && parent.children.length > 0
  const q = useDownlineParticularBooks({
    parentUserId: parent.id,
    marketId,
    selectedType: bookType,
    runners,
    marketType: type,
    enabled: live && !seeded,
  })
  const kids = seeded ? parent.children! : (q.data ?? [])
  const cols = padRunnerCount(runners.length, type)

  if (!seeded && q.isLoading && !q.data) {
    return (
      <tr className="w7-ws-tree-row" data-depth={depth + 1}>
        <td className="w7-ws-tree-user" style={{ paddingLeft: `${8 + (depth + 1) * 16}px` }}>
          Loading downline…
        </td>
        <td className="w7-ws-tree-expand" />
        <td className="w7-ws-tree-ne" />
        {Array.from({ length: cols }, (_, i) => (
          <td key={`load-${i}`} className="w7-ws-tree-cell" />
        ))}
      </tr>
    )
  }

  if (!seeded && q.isError) {
    return (
      <tr className="w7-ws-tree-row" data-depth={depth + 1}>
        <td
          className="w7-ws-tree-user"
          colSpan={3 + cols}
          style={{ paddingLeft: `${8 + (depth + 1) * 16}px` }}
        >
          Could not load downline
          {q.error instanceof Error && q.error.message ? `: ${q.error.message}` : ''}
        </td>
      </tr>
    )
  }

  return (
    <>
      {kids.map((child) => (
        <TreeRow
          key={child.id}
          node={child}
          runners={runners}
          type={type}
          bookType={bookType}
          depth={depth + 1}
          open={open}
          onToggle={onToggle}
          marketId={marketId}
          live={live}
          ladderOpen={ladderOpen}
          onToggleLadder={onToggleLadder}
          lineSession={lineSession}
        />
      ))}
    </>
  )
}

export function BookTree({
  book,
  bookType,
  live = false,
}: {
  book: WorkstationBook
  bookType: BookType
  /** Live agent session — family icon fetches downline via Agent-API. */
  live?: boolean
}) {
  // Lab starts with currentlyShowing=false (family icon visible, children hidden).
  const [open, setOpen] = useState<Set<string>>(() => new Set())
  const [ladderOpen, setLadderOpen] = useState<string | null>(null)
  const runners = book.type === 'match' ? visibleMatchRunners(book.runners) : book.runners
  const cols = padRunnerCount(runners.length, book.type)
  const lineSession = isLineSessionMarket({
    marketId: book.marketId,
    marketName: book.marketName,
    lineLadder: book.lineLadder,
  })

  const onToggle = (id: string) => {
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const onToggleLadder = (id: string) => {
    setLadderOpen((prev) => nextPtLadderOpen(prev, id))
  }

  return (
    <div className="w7-ws-tree">
      <div className="w7-ws-tree-market">{book.marketName}</div>
      <div className="w7-ws-tree-scroll">
        <table className="w7-ws-tree-table">
          <thead>
            <tr>
              <th className="w7-ws-tree-user" />
              <th className="w7-ws-tree-expand" />
              <th className="w7-ws-tree-ne">Net Exposure</th>
              {Array.from({ length: cols }, (_, i) => {
                const runner = runners[i]
                return (
                  <th key={runner?.id ?? `pad-${i}`} className="w7-ws-tree-cell">
                    {runner?.name ?? '\u00a0'}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            <TreeRow
              node={book.tree}
              runners={runners}
              type={book.type}
              bookType={bookType}
              depth={0}
              open={open}
              onToggle={onToggle}
              marketId={book.marketId}
              live={live}
              ladderOpen={ladderOpen}
              onToggleLadder={onToggleLadder}
              lineSession={lineSession}
            />
          </tbody>
        </table>
      </div>
    </div>
  )
}
